/**
 * ONE GPUDevice FOR THE PAGE — the renderer's and ORT's.
 *
 * WHY. A `GPUBuffer` belongs to the device that made it, so two devices cannot
 * share one byte. The OGS lift writes splats into GPU buffers on ORT's device
 * and never reads them back — that is the whole design — while the app's
 * `<Canvas>` built its own device at boot, before ORT existed. Hence a SECOND
 * canvas (`ThreeSplatBridge`) with a second renderer, composited by the browser
 * over the app's: a second sort, a per-frame camera copy, a resize to keep the
 * two in step, and no shared depth buffer — which is why a `renderOrder` in the
 * app scene could not put a Walk-here route line, a transform handle or a light
 * helper over the room, whatever it was set to.
 *
 * On a laptop it was also two GPUS: ORT asks for `high-performance` (the
 * discrete one) while three's own adapter request passes no preference, so the
 * face was decoded on one GPU and drawn on the other.
 *
 * So the device is made HERE, once, before either consumer, and handed to both:
 *
 *   - three takes it as `WebGPURenderer({ device })`. three then does NOT
 *     `destroy()` it on dispose — `WebGPUBackend.dispose` only destroys a device
 *     it created itself — which is what makes sharing safe in that direction.
 *   - ORT takes it as `env.webgpu.device`, set before the first session; and any
 *     consumer that asks the browser directly (the deformer plugin, hair) is
 *     answered by `installSharedGpuDevice()`, which returns this adapter from
 *     `requestAdapter` and this device from `requestDevice`.
 *
 * THE DEVICE IS A SUPERSET, AND THAT IS WHAT MAKES THE INTERCEPTION HONEST. It
 * is created with every feature the adapter reports and, by default, the
 * adapter's maximum for every limit, so any descriptor that adapter could have
 * satisfied, this device satisfies. A request it CANNOT satisfy is not quietly
 * under-served: `sharedDeviceSatisfies` says no, the caller gets a real device
 * of its own, and a console line names what was missing.
 *
 * Maxing the limits deliberately reverses the note in `webgpuLimits.js` ("asking
 * for more than you use is not free — the spec lets an implementation hand back
 * a slower device for a higher limit"). That note was about asking on behalf of
 * a pass that no longer existed. This device serves ORT, whose kernels we do not
 * author and whose needs we cannot enumerate, and which asks the adapter for
 * what it needs today — so granting less than the adapter allows is how a
 * decoder starts failing to allocate. `?gpulimits=default` requests the defaults
 * instead, so the trade can be measured rather than argued.
 *
 * `?gpudevice=0` turns the whole thing off: every consumer builds its own device
 * exactly as before. That is the bisect switch for "is this the device?".
 */

/** Not limits, though they sit on a `GPUSupportedLimits` prototype. */
const NOT_A_LIMIT = new Set(['constructor', '__brand'])

/** Our own calls must not be answered by our own patch. */
const PASSTHROUGH = Symbol.for('aos.gpu.passthrough')

let _adapterPromise = null
let _devicePromise = null
let _device = null
let _state = { state: 'idle', reason: null, limits: null, features: 0 }

function flag(name) {
  if (typeof location === 'undefined') return null
  try { return new URLSearchParams(location.search).get(name) } catch { return null }
}

/** Off entirely — every consumer makes its own device, as before. */
export function sharingDisabled() { return flag('gpudevice') === '0' }

/**
 * The adapter's maximum for every limit it reports.
 *
 * Reading the KEYS off the adapter's own limits object is what keeps this valid:
 * `requestDevice` rejects a limit name it does not know, and an adapter only
 * reports names it knows. Requesting exactly the reported value is legal for
 * every limit class, including the `min*` ones where "better" means smaller.
 *
 * @param {GPUSupportedLimits} supported
 * @returns {Record<string, number>}
 */
export function maxLimitsOf(supported) {
  const out = {}
  if (!supported) return out
  const proto = Object.getPrototypeOf(supported) ?? {}
  const names = new Set([...Object.getOwnPropertyNames(proto), ...Object.keys(supported)])
  for (const name of names) {
    if (NOT_A_LIMIT.has(name)) continue
    const value = supported[name]
    if (typeof value === 'number' && Number.isFinite(value)) out[name] = value
  }
  return out
}

/**
 * Can the shared device stand in for this request? PURE, and the rule the whole
 * interception rests on: every requested feature present, every requested limit
 * granted at least as high — except the `min*` limits, where a granted value
 * must be at most the one asked for.
 *
 * @param {GPUDevice|null} device
 * @param {GPUDeviceDescriptor} [descriptor]
 * @returns {{ok: boolean, missing?: string}}
 */
export function sharedDeviceSatisfies(device, descriptor = {}) {
  if (!device) return { ok: false, missing: 'no shared device' }
  for (const feature of descriptor?.requiredFeatures ?? []) {
    if (!device.features?.has?.(feature)) return { ok: false, missing: `feature ${feature}` }
  }
  for (const [name, wanted] of Object.entries(descriptor?.requiredLimits ?? {})) {
    if (typeof wanted !== 'number') continue
    const granted = device.limits?.[name]
    if (typeof granted !== 'number') return { ok: false, missing: `limit ${name}` }
    const isMinimum = name.startsWith('min')
    if (isMinimum ? granted > wanted : granted < wanted) {
      return { ok: false, missing: `limit ${name} (${granted} against ${wanted} requested)` }
    }
  }
  return { ok: true }
}

/** The one adapter. `high-performance` because the decoders want the discrete
 *  GPU, and because the alternative is the split this module exists to remove. */
export function sharedGpuAdapter() {
  if (!_adapterPromise) {
    _adapterPromise = (async () => {
      const gpu = typeof navigator !== 'undefined' ? navigator.gpu : null
      if (!gpu) return null
      try {
        const request = gpu[PASSTHROUGH] ?? gpu.requestAdapter.bind(gpu)
        return await request.call(gpu, { powerPreference: 'high-performance' })
      } catch (e) {
        console.warn('[gpu] adapter request failed:', e?.message ?? e)
        return null
      }
    })()
  }
  return _adapterPromise
}

/** The one device. Null when this browser has no WebGPU — three then uses its
 *  WebGL2 backend and ORT its wasm EP, both of which already handle that. */
export function requestSharedGpuDevice() {
  if (sharingDisabled()) return Promise.resolve(null)
  if (!_devicePromise) {
    _devicePromise = (async () => {
      const adapter = await sharedGpuAdapter()
      if (!adapter) {
        _state = { state: 'none', reason: 'no WebGPU adapter', limits: null, features: 0 }
        report()
        return null
      }
      // EVERY feature the adapter has — three does exactly this when it builds
      // its own device, and a shared one has to match: without
      // `texture-compression-bc` the KTX2 ladder stops loading, and without
      // `shader-f16` the fp16 decoders refuse to build.
      const requiredFeatures = [...(adapter.features ?? [])]
      const wantDefaults = flag('gpulimits') === 'default'
      const requiredLimits = wantDefaults ? undefined : maxLimitsOf(adapter.limits)
      try {
        const request = adapter[PASSTHROUGH] ?? Object.getPrototypeOf(adapter).requestDevice
        const device = await request.call(adapter, {
          label: 'aos-shared', requiredFeatures, requiredLimits,
        })
        _device = device
        _state = {
          state: 'shared',
          reason: null,
          limits: wantDefaults ? 'default' : 'adapter maximum',
          features: requiredFeatures.length,
        }
        console.info(`[gpu] one device for the page: ${requiredFeatures.length} features, `
          + `${wantDefaults ? 'default' : 'adapter-maximum'} limits`)
        report()
        return device
      } catch (e) {
        // Not fatal to anyone — both consumers still build their own below. It IS
        // loud, because it puts the page back on two devices and two canvases.
        console.error('[gpu] the shared device could not be created; the renderer and ORT will '
          + 'each build their own and the splats stay on a second canvas:', e?.message ?? e)
        _state = { state: 'error', reason: String(e?.message ?? e), limits: null, features: 0 }
        report()
        return null
      }
    })()
  }
  return _devicePromise
}

/** The device if it exists already, else null — for code that cannot await. */
export function sharedGpuDeviceSync() { return _device }

/**
 * Answer the browser's own entry points with the shared adapter and device.
 *
 * The only way to reach a consumer that neither takes a device from us nor goes
 * through our code: the deformer plugin builds its own ORT session, and ORT's
 * wasm asks for a device itself on any path that was not handed one. It is the
 * same interception point, and the same reasoning about WHEN, as the
 * `requestDevice` patch in `webgpuLimits.js` — ORT caches its device on the
 * FIRST WebGPU session create, so a later patch is too late.
 *
 * Idempotent. Never throws.
 */
export function installSharedGpuDevice() {
  if (typeof navigator === 'undefined' || !navigator.gpu) return
  if (sharingDisabled()) {
    console.warn('[gpu] ?gpudevice=0 — every consumer builds its own device, '
      + 'and the splats are drawn on a second canvas')
    return
  }
  const gpu = navigator.gpu
  if (!gpu[PASSTHROUGH] && typeof gpu.requestAdapter === 'function') {
    gpu[PASSTHROUGH] = gpu.requestAdapter.bind(gpu)
    gpu.requestAdapter = async function requestAdapter(options) {
      const shared = await sharedGpuAdapter()
      return shared ?? gpu[PASSTHROUGH](options)
    }
  }
  const proto = typeof GPUAdapter !== 'undefined' ? GPUAdapter.prototype : null
  if (proto && !proto[PASSTHROUGH] && typeof proto.requestDevice === 'function') {
    const real = proto.requestDevice
    proto[PASSTHROUGH] = real
    proto.requestDevice = async function requestDevice(descriptor = {}) {
      const shared = await requestSharedGpuDevice()
      const verdict = sharedDeviceSatisfies(shared, descriptor)
      if (verdict.ok) return shared
      if (shared) {
        // NOT silently under-served. A caller needing more than this device
        // grants gets a real one of its own — worth saying out loud, since that
        // is how the second device, the second canvas and every symptom that
        // comes with them would return.
        console.warn(`[gpu] a caller asked for more than the shared device grants `
          + `(${verdict.missing}) — it gets its own device`)
      }
      return real.call(this, descriptor)
    }
  }
  report()
}

function report() {
  if (typeof window === 'undefined') return
  window.__GPU_DEVICE__ = {
    ..._state,
    get device() { return _device },
    get adapterInfo() { return _device?.adapterInfo ?? null },
  }
}

report()
